/**
 * The ad rotation: a shuffled queue, drawn down one at a time.
 *
 * Independent random picks feel wrong at this scale. With seventeen banners a
 * fair coin repeats the one you just saw about six percent of the time, and
 * over a Sunday of refreshing you will see three of them twice before you see
 * six of them at all. That reads as broken, not as random. So the order is
 * decided once per session, and every ad is shown before any is shown again.
 *
 * The queue lives in `sessionStorage` because that is what a session means
 * here: it survives a refresh, which is the case being asked for, and it is
 * gone by the next visit, so a new sitting gets a new order rather than
 * resuming last week's. An in-memory copy backs it for private-mode browsers,
 * where the module lives as long as the tab does and only a hard refresh
 * restarts the cycle.
 *
 * Draws are per mount, so an in-app navigation advances the queue as well as a
 * refresh does. Both are "showing the ad slot again", and both should show
 * something new.
 */

import { ADS, type Ad } from './ads'

const QUEUE_KEY = 'sfg.ads.queue'
const LAST_KEY = 'sfg.ads.last'

/** Survives a re-render and a route change; lost on refresh. */
let memoryQueue: string[] | null = null
let memoryLast: string | null = null

function readQueue(): string[] | null {
  try {
    const raw = sessionStorage.getItem(QUEUE_KEY)
    if (raw) {
      const parsed: unknown = JSON.parse(raw)
      if (Array.isArray(parsed) && parsed.every((v) => typeof v === 'string')) return parsed as string[]
    }
    return null
  } catch {
    return memoryQueue
  }
}

function writeQueue(queue: string[], last: string | null): void {
  memoryQueue = queue
  memoryLast = last
  try {
    sessionStorage.setItem(QUEUE_KEY, JSON.stringify(queue))
    if (last) sessionStorage.setItem(LAST_KEY, last)
  } catch {
    // Private mode. The in-memory copy above is the whole story from here.
  }
}

function readLast(): string | null {
  try {
    return sessionStorage.getItem(LAST_KEY) ?? memoryLast
  } catch {
    return memoryLast
  }
}

/** Fisher-Yates. Every permutation equally likely, which the naive sort is not. */
function shuffled<T>(items: readonly T[]): T[] {
  const out = items.slice()
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[out[i], out[j]] = [out[j], out[i]]
  }
  return out
}

function freshCycle(avoidFirst: string | null): string[] {
  const all = ADS.map((a) => a.src)
  if (all.length <= 1) return all
  const order = shuffled(all)
  // The seam between two cycles is the one place this scheme can still repeat:
  // the last of one shuffle and the first of the next are independent. Swap the
  // head forward when they collide.
  if (avoidFirst && order[0] === avoidFirst) {
    const swapWith = 1 + Math.floor(Math.random() * (order.length - 1))
    ;[order[0], order[swapWith]] = [order[swapWith], order[0]]
  }
  return order
}

/**
 * The next ad in this session's order, refilling when the cycle is spent.
 *
 * Entries that are no longer in `ADS` are dropped rather than trusted: a
 * deployment that retires an ad leaves its filename in a session's queue, and
 * a 404 in the slot is worse than a slightly short cycle.
 */
export function nextAd(): Ad | undefined {
  if (ADS.length === 0) return undefined

  const known = new Set(ADS.map((a) => a.src))
  let queue = (readQueue() ?? []).filter((src) => known.has(src))
  if (queue.length === 0) queue = freshCycle(readLast())

  const [src, ...rest] = queue
  writeQueue(rest, src)
  return ADS.find((a) => a.src === src)
}
